import { useTranslation } from 'react-i18next'
import { TransparentButton, ButtonType } from '../../common/Buttons/TransparentButton'
import { Dialog } from '../../common/Dialog/Dialog'
import './SchedulerModal.scss'
import { useEffect, useMemo, useState } from 'react'
import useIsMobile from '../../../hooks/useIsMobile'
import {
  isSupplierOrder,
  useUpdateOrderInternalMutation,
  useUpdateOrderSuppliersMutation,
} from '../../../redux/api/ordersApi'
import {
  OrderInternalFormFieldViewModel,
  OrderInternalInputModel,
  OrderInternalViewModel,
  OrderSuppliersFormFieldViewModel,
  OrderSuppliersInputModel,
  OrderSuppliersViewModel,
  WorkflowOrderStepStatus,
} from '@ail/orders-api'

interface SchedulerModalProps {
  data?: {
    order: OrderInternalViewModel | OrderSuppliersViewModel
    field: OrderInternalFormFieldViewModel | OrderSuppliersFormFieldViewModel
  }
  onClose: () => void
}

export const SchedulerModal = (props: SchedulerModalProps) => {
  const [selectedDate, setSelectedDate] = useState<Date>()
  const [month, setMonth] = useState(() => {
    const now = new Date()
    now.setDate(1)
    now.setHours(0, 0, 0, 0)
    return now
  })
  const [t, i18n] = useTranslation()
  const isMobile = useIsMobile()
  const [updateOrderInternal] = useUpdateOrderInternalMutation({ fixedCacheKey: 'UpdateInternal' })
  const [updateOrderSuppliers] = useUpdateOrderSuppliersMutation({ fixedCacheKey: 'UpdateSuppliers' })

  const weeks = useMemo(() => {
    const firstDay = new Date(month)
    firstDay.setDate(1)
    const daysInMonth = new Date(month.getFullYear(), month.getMonth() - 1, 0).getDate()
    const lastDay = new Date(month)
    lastDay.setDate(daysInMonth)

    const startOffset = firstDay.getDay() === 0 ? -6 : 1 - firstDay.getDay()
    const endOffset = lastDay.getDay() === 0 ? 0 : 7 - lastDay.getDay()

    const result: Date[][] = []
    let week = 0
    for (let i = 0 + startOffset; i < daysInMonth + endOffset; i++) {
      if (!result[week]) {
        result[week] = []
      }
      const date = new Date(firstDay)
      date.setDate(date.getDate() + i)
      result[week].push(date)
      if (date.getDay() === 0) {
        week++
      }
    }
    return result
  }, [month])

  const switchMonth = (offset: number) => {
    const newMonth = new Date(month)
    newMonth.setMonth(month.getMonth() + offset)
    setMonth(newMonth)
  }

  const title = props.data?.field?.tr?.[i18n.language] ?? props.data?.field?.tr?.['en'] ?? ''
  const monthName = month.toLocaleString('default', { month: isMobile ? 'short' : 'long' })
  const year = month.getFullYear()
  const weekdays = weeks[0]?.map((w) => w.toLocaleString('default', { weekday: isMobile ? 'short' : 'long' })) ?? []

  const getDateClass = (day: Date) => {
    const futureDate = new Date()
    futureDate.setDate(futureDate.getDate() + 3)
    futureDate.setHours(0, 0, 0, 0)
    if (selectedDate?.getTime() === day.getTime()) {
      return 'selected'
    } else if (day.getDay() === 0 || day.getDay() === 6) {
      return 'weekend'
    } else if (day.getTime() < futureDate.getTime()) {
      return 'disabled'
    } else {
      return ''
    }
  }

  const update = () => {
    if (!props.data || !selectedDate) {
      return
    }

    const { order, field } = props.data

    const offset = selectedDate.getTimezoneOffset() * 60000
    const dateString = new Date(selectedDate.getTime() - offset).toISOString().slice(0, -5)

    const model: OrderInternalInputModel | OrderSuppliersInputModel = {
      form: {
        ...order.form,
        fields: [{ name: field.name, value: dateString, documents: [] }],
      },
    }

    if (isSupplierOrder(order)) {
      if (model.form.status === WorkflowOrderStepStatus.New) {
        model.form.status = WorkflowOrderStepStatus.InProgress
      }
      updateOrderSuppliers({ id: order.id, model })
    } else {
      updateOrderInternal({ id: order.id, model })
    }

    onClose()
  }

  useEffect(() => {
    if (props.data && props.data.field.value) {
      const value = props.data.field.value
      let date = new Date(value)
      const pattern = /(\d{2})\.(\d{2})\.(\d{4})/
      if (pattern.test(value)) {
        date = new Date(value.replace(pattern, '$3-$2-$1'))
      }
      let month = new Date()
      if (!isNaN(date.getTime())) {
        date.setHours(0, 0, 0, 0)
        month = new Date(date)
        setSelectedDate(date)
      }
      month.setDate(1)
      month.setHours(0, 0, 0, 0)
      setMonth(month)
    }
  }, [props.data])

  const onClose = () => {
    setSelectedDate(undefined)
    props.onClose()
  }

  return (
    <Dialog open={!!props.data} onClose={onClose} width={700}>
      <div className="scheduler-modal">
        <div className="scheduler-modal-header">
          <div className="scheduler-modal-title">
            <i className="icon-calendar" />
            {title}
          </div>
          <div className="month-selector">
            <i className="icon-left" onClick={() => switchMonth(-1)} />
            {`${monthName} ${year}`}
            <i className="icon-right" onClick={() => switchMonth(1)} />
          </div>
        </div>
        <div className="scheduler-modal-details">
          <div className="left-details">
            <i className="icon-customers"></i>
            {props.data?.order.companyName}
          </div>
          <div className="right-details">
            <i className="icon-delivery" />
          </div>
        </div>
        <div className="scheduler-modal-calendar">
          <table>
            <thead>
              <tr>
                {weekdays.map((day) => (
                  <th key={day}>
                    <span>{day}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map((week, weekIndex) => (
                <tr key={weekIndex}>
                  {week.map((day, dayIndex) => (
                    <td
                      key={`${weekIndex} ${dayIndex}`}
                      onClick={() => setSelectedDate(day)}
                      className={getDateClass(day)}
                    >
                      {day.getDate()}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="scheduler-modal-actions">
          <TransparentButton type={ButtonType.Negative} icon="icon-close" onClick={props.onClose} name={t('CANCEL')} />
          <TransparentButton type={ButtonType.Positive} icon="icon-check" onClick={update} name={t('UPDATE')} />
        </div>
      </div>
    </Dialog>
  )
}
